import { useRef, useState } from "react";
import { Check, CircleNotch, Eye, FileJs, Upload } from "@phosphor-icons/react";
import { coverLetterDataSchema, type CoverLetterData } from "@reactive-resume/schema";
import {
  buttonVariants,
  Card,
  CardContent,
  CardDescription,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  ScrollArea,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { ZodError } from "zod";

import { useToast } from "@/client/hooks/use-toast";
import { useCoverLetterStore } from "@/client/stores/cover-letter";
import { CLCollapsibleSection } from "../../../CollapsibleSection";

type ValidationResult =
  | {
      isValid: false;
      errors: string;
    }
  | {
      isValid: true;
      result: CoverLetterData;
    };

export const CLImportSection = () => {
  const { toast } = useToast();
  const setValue = useCoverLetterStore((state) => state.setValue);
  const currentData = useCoverLetterStore((state) => state.coverLetter.data);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setValidationResult(null);
    validateFile(file);
  };

  const validateFile = async (file: File) => {
    try {
      const text = await file.text();
      const rawJson = JSON.parse(text);

      // Handle raw CoverLetterData or CoverLetterDto with .data property
      const targetData = rawJson.data ? rawJson.data : rawJson;

      const parsed = coverLetterDataSchema.safeParse(targetData);
      if (!parsed.success) {
        setValidationResult({
          isValid: false,
          errors: parsed.error.issues.map((issue) => `${issue.path.join(".")}: ${issue.message}`).join("\n"),
        });

        toast({
          variant: "error",
          title: "Invalid file format",
          description: "The selected file is not a valid Cover Letter JSON file.",
        });
        return;
      }

      setValidationResult({ isValid: true, result: parsed.data });

      toast({
        variant: "success",
        title: "File validated successfully",
        description: "Your cover letter file is valid and ready to import.",
      });
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      setValidationResult({
        isValid: false,
        errors: errorMsg,
      });

      toast({
        variant: "error",
        title: "Invalid JSON",
        description: "Could not read the JSON file.",
      });
    }
  };

  const generateChangePreview = () => {
    if (!validationResult?.isValid) return [];

    const importedData = validationResult.result;
    const changes: Array<{
      section: string;
      type: "added" | "modified" | "removed";
      description: string;
      diff?: { before: string; after: string; field?: string };
    }> = [];

    const createDiff = (before: string, after: string, field?: string) => ({
      before: before || "",
      after: after || "",
      field,
    });

    // Sender comparisons
    if (currentData.sender?.name !== importedData.sender?.name) {
      changes.push({
        section: "Sender",
        type: "modified",
        description: "Sender name will be updated",
        diff: createDiff(currentData.sender?.name, importedData.sender?.name, "Name"),
      });
    }

    if (currentData.sender?.email !== importedData.sender?.email) {
      changes.push({
        section: "Sender",
        type: "modified",
        description: "Sender email will be updated",
        diff: createDiff(currentData.sender?.email, importedData.sender?.email, "Email"),
      });
    }

    // Recipient comparisons
    if (currentData.recipient?.name !== importedData.recipient?.name) {
      changes.push({
        section: "Recipient",
        type: "modified",
        description: "Recipient name will be updated",
        diff: createDiff(currentData.recipient?.name, importedData.recipient?.name, "Recipient Name"),
      });
    }

    if (currentData.recipient?.company !== importedData.recipient?.company) {
      changes.push({
        section: "Recipient",
        type: "modified",
        description: "Company will be updated",
        diff: createDiff(currentData.recipient?.company, importedData.recipient?.company, "Company"),
      });
    }

    // Letter comparisons
    if (currentData.letter?.subject !== importedData.letter?.subject) {
      changes.push({
        section: "Letter",
        type: "modified",
        description: "Subject will be updated",
        diff: createDiff(currentData.letter?.subject, importedData.letter?.subject, "Subject"),
      });
    }

    if (currentData.letter?.date !== importedData.letter?.date) {
      changes.push({
        section: "Letter",
        type: "modified",
        description: "Date will be updated",
        diff: createDiff(currentData.letter?.date, importedData.letter?.date, "Date"),
      });
    }

    if (currentData.letter?.greeting !== importedData.letter?.greeting) {
      changes.push({
        section: "Letter",
        type: "modified",
        description: "Greeting will be updated",
        diff: createDiff(currentData.letter?.greeting, importedData.letter?.greeting, "Greeting"),
      });
    }

    if (currentData.letter?.opening !== importedData.letter?.opening) {
      changes.push({
        section: "Letter",
        type: "modified",
        description: "Opening paragraph will be updated",
        diff: createDiff(currentData.letter?.opening, importedData.letter?.opening, "Opening"),
      });
    }

    if (currentData.letter?.body !== importedData.letter?.body) {
      changes.push({
        section: "Letter",
        type: "modified",
        description: "Letter body will be updated",
        diff: createDiff(currentData.letter?.body, importedData.letter?.body, "Body"),
      });
    }

    if (currentData.letter?.closing !== importedData.letter?.closing) {
      changes.push({
        section: "Letter",
        type: "modified",
        description: "Closing paragraph will be updated",
        diff: createDiff(currentData.letter?.closing, importedData.letter?.closing, "Closing"),
      });
    }

    // Reference comparisons
    if (currentData.reference?.jobTitle !== importedData.reference?.jobTitle) {
      changes.push({
        section: "Reference",
        type: "modified",
        description: "Job title will be updated",
        diff: createDiff(currentData.reference?.jobTitle, importedData.reference?.jobTitle, "Job Title"),
      });
    }

    // Custom sections comparisons
    const currentCustomKeys = Object.keys(currentData.custom || {});
    const importedCustomKeys = Object.keys(importedData.custom || {});

    if (currentCustomKeys.length !== importedCustomKeys.length) {
      changes.push({
        section: "Custom Sections",
        type: currentCustomKeys.length < importedCustomKeys.length ? "added" : "removed",
        description: `Custom sections count: ${currentCustomKeys.length} → ${importedCustomKeys.length}`,
        diff: createDiff(
          `${currentCustomKeys.length} custom sections`,
          `${importedCustomKeys.length} custom sections`,
          "Custom Sections Count",
        ),
      });
    }

    return changes;
  };

  const handleConfirmImport = async () => {
    if (!validationResult?.isValid || !selectedFile) return;

    setLoading(true);
    setShowPreview(false);

    try {
      const importedData = validationResult.result;

      // Update the cover letter store
      setValue("sender", importedData.sender);
      setValue("recipient", importedData.recipient);
      setValue("letter", importedData.letter);
      setValue("reference", importedData.reference);
      setValue("custom", importedData.custom || {});
      setValue("metadata", importedData.metadata);

      setSelectedFile(null);
      setValidationResult(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      toast({
        variant: "success",
        title: "Cover letter imported successfully",
        description: "Your cover letter has been updated with the imported data.",
      });
    } catch (error: unknown) {
      toast({
        variant: "error",
        title: "Import failed",
        description: error instanceof Error ? error.message : "An error occurred while importing.",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetImport = () => {
    setSelectedFile(null);
    setValidationResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const headerHTML = (
    <div className="flex items-center gap-x-4">
      <Upload size={18} />
      <span className="line-clamp-1 text-base font-bold text-gray-800 lg:text-lg">Import</span>
    </div>
  );

  const contentHTML = (
    <div className="grid gap-y-4">
      <Card
        className={cn(
          buttonVariants({ variant: "ghost" }),
          "h-auto cursor-pointer flex-row items-center gap-x-5 px-4 pb-3 pt-2 text-left",
          loading && "pointer-events-none cursor-progress opacity-75",
        )}
        onClick={handleFileSelect}
      >
        {loading ? (
          <CircleNotch size={22} className="animate-spin" />
        ) : validationResult?.isValid ? (
          <Check size={22} className="text-green-600" />
        ) : (
          <Upload size={22} />
        )}

        <CardContent className="flex-1 p-0">
          <CardTitle className="text-sm">
            {selectedFile ? selectedFile.name : "Select Cover Letter File"}
          </CardTitle>
          <CardDescription className="font-normal text-xs text-muted-foreground mt-1">
            {validationResult?.isValid
              ? 'File validated. Click "Import Now" below to update your cover letter.'
              : "Upload a Reactive Resume JSON file to import your cover letter data. You can export this format from the Export section above."}
          </CardDescription>
        </CardContent>
      </Card>

      {selectedFile && (
        <div className="flex gap-x-2">
          {validationResult?.isValid && (
            <>
              <Card
                className={cn(
                  buttonVariants({ variant: "outline" }),
                  "h-auto cursor-pointer flex-row items-center justify-center gap-x-2 px-3 py-1.5",
                )}
                onClick={() => setShowPreview(true)}
              >
                <Eye size={16} />
                <span className="text-xs font-medium">Preview Changes</span>
              </Card>

              <Card
                className={cn(
                  buttonVariants({ variant: "primary" }),
                  "h-auto cursor-pointer flex-row items-center justify-center gap-x-2 px-3 py-1.5",
                  loading && "pointer-events-none cursor-progress opacity-75",
                )}
                onClick={handleConfirmImport}
              >
                {loading ? <CircleNotch size={16} className="animate-spin" /> : <FileJs size={16} />}
                <span className="text-xs font-medium">Import Now</span>
              </Card>
            </>
          )}

          <Card
            className={cn(
              buttonVariants({ variant: "outline" }),
              "h-auto cursor-pointer flex-row items-center justify-center gap-x-2 px-3 py-1.5",
            )}
            onClick={resetImport}
          >
            <span className="text-xs font-medium">Cancel</span>
          </Card>
        </div>
      )}

      {validationResult?.isValid === false && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs">
          <p className="font-medium text-red-700">Validation Error</p>
          <p className="mt-1 text-red-600">
            Please ensure you are uploading a valid Cover Letter JSON file.
          </p>
          <pre className="mt-2 max-h-32 overflow-auto font-mono text-[10px] text-red-800">
            {validationResult.errors}
          </pre>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={handleFileChange}
        className="hidden"
        aria-label="Select cover letter file to import"
      />

      {/* Changes Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Preview Changes</DialogTitle>
            <DialogDescription>
              Review the changes that will be made to your cover letter before confirming the import.
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="max-h-96">
            <div className="space-y-3">
              {(() => {
                const changes = generateChangePreview();

                if (changes.length === 0) {
                  return (
                    <div className="py-8 text-center text-muted-foreground">
                      <p>No changes detected. The imported data is identical to your current cover letter.</p>
                    </div>
                  );
                }

                return changes.map((change, index) => (
                  <div key={index} className="overflow-hidden rounded-md border">
                    <div className="flex items-center gap-3 bg-muted/30 p-3">
                      <div
                        className={cn(
                          "h-2 w-2 flex-shrink-0 rounded-full",
                          change.type === "added" && "bg-green-500",
                          change.type === "modified" && "bg-yellow-500",
                          change.type === "removed" && "bg-red-500",
                        )}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="mb-1 flex items-center gap-2">
                          <span className="text-sm font-medium">{change.section}</span>
                          <span
                            className={cn(
                              "rounded-full px-2 py-0.5 text-xs",
                              change.type === "added" && "bg-green-100 text-green-700",
                              change.type === "modified" && "bg-yellow-100 text-yellow-700",
                              change.type === "removed" && "bg-red-100 text-red-700",
                            )}
                          >
                            {change.type === "added" && "Added"}
                            {change.type === "modified" && "Modified"}
                            {change.type === "removed" && "Removed"}
                          </span>
                        </div>
                        <p className="break-words text-sm text-muted-foreground">
                          {change.description}
                        </p>
                      </div>
                    </div>

                    {change.diff && (
                      <div className="border-t font-mono text-xs">
                        {change.diff.field && (
                          <div className="border-b bg-muted/20 px-3 py-1 text-xs font-medium text-muted-foreground">
                            {change.diff.field}
                          </div>
                        )}
                        {change.diff.before && (change.type === "removed" || change.type === "modified") && (
                          <div className="border-l-4 border-red-400 bg-red-50 px-3 py-2">
                            <div className="flex items-start gap-2">
                              <span className="select-none font-bold text-red-600">-</span>
                              <pre className="flex-1 whitespace-pre-wrap break-words text-red-700">
                                {change.diff.before}
                              </pre>
                            </div>
                          </div>
                        )}
                        {change.diff.after && (change.type === "added" || change.type === "modified") && (
                          <div className="border-l-4 border-green-400 bg-green-50 px-3 py-2">
                            <div className="flex items-start gap-2">
                              <span className="select-none font-bold text-green-600">+</span>
                              <pre className="flex-1 whitespace-pre-wrap break-words text-green-700">
                                {change.diff.after}
                              </pre>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ));
              })()}
            </div>
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmImport} disabled={loading} className="gap-2">
              {loading ? <CircleNotch size={16} className="animate-spin" /> : <FileJs size={16} />}
              Confirm Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  return <CLCollapsibleSection id="cl-import" header={headerHTML} content={contentHTML} />;
};
